import { create } from 'zustand';
import { apiClient } from './api-client';

export interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: AuthUser;
}

export interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  guestMode: boolean;

  setTokens: (access: string, refresh: string) => void;
  setUser: (user: AuthState['user']) => void;
  login: (email: string, password: string) => Promise<void>;
  continueAsGuest: () => void;
  logout: () => void;
  initFromPostMessage: () => void;
  initFromSession: () => void;
  fetchUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  refreshToken: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,
  guestMode: sessionStorage.getItem('guest_mode') === '1',

  setTokens: (access: string, refresh: string) => {
    sessionStorage.setItem('access_token', access);
    sessionStorage.setItem('refresh_token', refresh);
    set({
      token: access,
      refreshToken: refresh,
      isAuthenticated: true,
    });
  },

  setUser: (user: AuthState['user']) => {
    set({
      user,
      isAuthenticated: user !== null,
    });
  },

  login: async (email: string, password: string) => {
    // apiClient's response interceptor unwraps the { success, data } envelope,
    // so the resolved value IS the payload despite the AxiosResponse typing.
    const data = (await apiClient.post('/auth/login', {
      email,
      password,
    })) as unknown as LoginResponse;

    get().setTokens(data.access_token, data.refresh_token);
    sessionStorage.removeItem('guest_mode');
    set({ user: data.user, guestMode: false });
  },

  continueAsGuest: () => {
    sessionStorage.setItem('guest_mode', '1');
    set({ guestMode: true });
  },

  logout: () => {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('guest_mode');
    set({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      guestMode: false,
    });
  },

  initFromPostMessage: () => {
    const allowedOrigin = new URL(
      import.meta.env.VITE_HITFACTOR_URL ||
        import.meta.env.VITE_RANKSHOOT_URL ||
        'http://localhost:3000',
    ).origin;

    const handlePostMessage = (event: MessageEvent) => {
      // Validate origin
      if (event.origin !== allowedOrigin) {
        console.warn('Unauthorized postMessage origin:', event.origin);
        return;
      }

      const { type, access_token, refresh_token } = event.data;

      if (type === 'auth_token' && access_token && refresh_token) {
        get().setTokens(access_token, refresh_token);
        get().fetchUser();
        // Ack so the parent stops re-sending the token handoff
        (event.source as Window | null)?.postMessage(
          { type: 'auth_token_received' },
          event.origin,
        );
      }
    };

    window.addEventListener('message', handlePostMessage);

    // Signal readiness to the opener so it can post tokens without racing
    // against this listener being registered
    if (window.opener) {
      try {
        (window.opener as Window).postMessage({ type: 'designer_ready' }, allowedOrigin);
      } catch {
        // Opener may be gone or cross-origin-restricted — the parent's
        // retry loop covers this case
      }
    }

    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
  },

  initFromSession: async () => {
    set({ isLoading: true });
    try {
      const token = sessionStorage.getItem('access_token');
      const refreshToken = sessionStorage.getItem('refresh_token');

      if (token && refreshToken) {
        set({
          token,
          refreshToken,
          isAuthenticated: true,
        });
        await get().fetchUser();
      } else {
        set({ isAuthenticated: false });
      }
    } catch (error) {
      console.error('Failed to initialize auth from session:', error);
      set({ isAuthenticated: false });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchUser: async () => {
    try {
      // Interceptor unwraps the envelope — the resolved value is the user object.
      const user = (await apiClient.get('/users/me')) as unknown as AuthUser;
      get().setUser(user ?? null);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      get().logout();
    }
  },
}));
